export const FLYTHROUGH_AZIMUTH_PRESETS = [0, 45, 90, 135, 180, 225, 270, 315];
export const FLYTHROUGH_ELEVATION_PRESETS = [-30, 0, 30, 60];
export const FLYTHROUGH_DISTANCE_PRESETS = [0.6, 1, 1.8];

export const FLYTHROUGH_CANONICAL_CAMERA_ANGLE = {
  azimuth: 0,
  elevation: 0,
  distance: 1,
};

export function normalizeFlythroughCameraAngle(angle = {}) {
  return {
    azimuth: Math.round(Number(angle?.azimuth) || 0),
    elevation: Math.round(Number(angle?.elevation) || 0),
    distance: Number(angle?.distance) || 1,
  };
}

function snapToPreset(value, presets) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return presets[0];
  let best = presets[0];
  let bestDist = Infinity;
  for (const preset of presets) {
    const dist = Math.abs(preset - numeric);
    if (dist < bestDist) {
      bestDist = dist;
      best = preset;
    }
  }
  return best;
}

export function snapFlythroughCameraAngle(angle = {}) {
  return {
    azimuth: snapToPreset(angle?.azimuth, FLYTHROUGH_AZIMUTH_PRESETS),
    elevation: snapToPreset(angle?.elevation, FLYTHROUGH_ELEVATION_PRESETS),
    distance: snapToPreset(angle?.distance, FLYTHROUGH_DISTANCE_PRESETS),
  };
}

function findPresetIndex(value, presets, { circular = false } = {}) {
  let bestIdx = 0;
  let bestDist = Infinity;
  for (let i = 0; i < presets.length; i++) {
    const raw = Math.abs(presets[i] - value);
    const dist = circular ? Math.min(raw, 360 - raw) : raw;
    if (dist < bestDist) {
      bestDist = dist;
      bestIdx = i;
    }
  }
  return bestIdx;
}

function isSinglePresetStep(prevValue, nextValue, presets, { circular = false } = {}) {
  const prevIdx = findPresetIndex(prevValue, presets, { circular });
  const nextIdx = findPresetIndex(nextValue, presets, { circular });
  const diff = Math.abs(nextIdx - prevIdx);
  if (circular && presets.length > 1) {
    const wrapDiff = presets.length - diff;
    return diff === 1 || wrapDiff === 1;
  }
  return diff === 1;
}

export function isValidFlythroughAngleStep(previousAngle, nextAngle) {
  const prev = snapFlythroughCameraAngle(previousAngle);
  const next = snapFlythroughCameraAngle(nextAngle);

  const azimuthChanged = prev.azimuth !== next.azimuth;
  const elevationChanged = prev.elevation !== next.elevation;
  const distanceChanged = prev.distance !== next.distance;
  const changedCount = [azimuthChanged, elevationChanged, distanceChanged].filter(Boolean).length;

  if (changedCount !== 1) return false;

  if (azimuthChanged) {
    return isSinglePresetStep(prev.azimuth, next.azimuth, FLYTHROUGH_AZIMUTH_PRESETS, {
      circular: true,
    });
  }
  if (elevationChanged) {
    return isSinglePresetStep(prev.elevation, next.elevation, FLYTHROUGH_ELEVATION_PRESETS);
  }
  return isSinglePresetStep(prev.distance, next.distance, FLYTHROUGH_DISTANCE_PRESETS);
}

export function formatFlythroughCameraAngleLabel(angle = {}) {
  const safe = snapFlythroughCameraAngle(angle);
  const azimuthLabels = {
    0: 'Front',
    45: 'Front-right',
    90: 'Right',
    135: 'Back-right',
    180: 'Back',
    225: 'Back-left',
    270: 'Left',
    315: 'Front-left',
  };
  const elevationLabels = {
    [-30]: 'Low-angle',
    0: 'Eye-level',
    30: 'Elevated',
    60: 'High-angle',
  };
  const distanceLabels = {
    0.6: 'Close-up',
    1: 'Medium',
    1.8: 'Wide',
  };
  return `${azimuthLabels[safe.azimuth] ?? `${safe.azimuth}°`}, ${
    elevationLabels[safe.elevation] ?? `${safe.elevation}°`
  }, ${distanceLabels[safe.distance] ?? safe.distance}`;
}

export function validateFlythroughCameraAngleSequence(sequence = []) {
  if (sequence.length < 2) {
    return 'Add at least one camera angle after the original view.';
  }
  if (sequence.length > 6) {
    return 'Camera angle sequences support up to 6 angles.';
  }

  const canonical = snapFlythroughCameraAngle(FLYTHROUGH_CANONICAL_CAMERA_ANGLE);
  const first = snapFlythroughCameraAngle(sequence[0]);
  if (
    first.azimuth !== canonical.azimuth ||
    first.elevation !== canonical.elevation ||
    first.distance !== canonical.distance
  ) {
    return 'The first camera angle must remain the original view.';
  }

  for (let index = 1; index < sequence.length; index += 1) {
    if (!isValidFlythroughAngleStep(sequence[index - 1], sequence[index])) {
      return 'Each new angle must change only one of azimuth, elevation, or distance by one step.';
    }
  }

  return null;
}
